import { ButtonBase, styled, Tooltip } from '@mui/material'
import { forwardRef } from 'react'
import { CountBadge } from '@omnea-app/omui/CountBadge'
import { focusRing } from '@omnea-app/omui/focusRing'
import { StatusDot } from '@omnea-app/omui/StatusDot'
import { useSideNavContext } from './SideNavContext'
import type { ButtonBaseProps } from '@mui/material'
import type { ElementType, ReactNode } from 'react'

type NavItemProps = {
  icon: ReactNode
  label: string
  /** The item leads to the page that is open. */
  selected?: boolean
  /** Shown as a badge, or as a dot on the icon rail. */
  count?: number
  /** A short label pill after the name, e.g. "New". Hidden on the icon rail. */
  badge?: string
  href?: string
  /** Renders `href`, e.g. Next's `Link`, so navigating stays client-side. */
  LinkComponent?: ElementType
} & Pick<ButtonBaseProps, 'onClick'>

// A group's header row also opens what it controls.
type NavItemRowProps = NavItemProps &
  Pick<ButtonBaseProps, 'onMouseEnter' | 'onMouseLeave' | 'onKeyDown' | 'aria-expanded' | 'aria-controls' | 'aria-current'>

type OwnerState = { selected: boolean; collapsed: boolean }

/** The row's inline padding, as a `theme.spacing` factor. */
export const ROW_PADDING_INLINE = 2
export const ICON_SLOT_SIZE = '1.25rem'

const NavItemRoot = styled(ButtonBase, { shouldForwardProp: (prop) => prop !== 'ownerState' })<{ ownerState: OwnerState }>(
  ({ theme, ownerState }) => ({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: ownerState.collapsed ? 'center' : 'flex-start',
    gap: theme.spacing(1.5),
    width: ownerState.collapsed ? '2.25rem' : '100%',
    height: '2.25rem',
    flexShrink: 0,
    // Left-anchored on the rail too, so icons hold their place while the nav narrows instead of jumping to
    // its still-wide centre; the rail's padding centres them once it lands.
    alignSelf: ownerState.collapsed ? 'flex-start' : 'stretch',
    padding: theme.spacing(1.5, ROW_PADDING_INLINE),
    borderRadius: theme.shape['radius-md'],
    ...theme.typography['body-medium'],
    textAlign: 'left',
    color: ownerState.selected ? theme.palette.text.default : theme.palette.text.muted,
    backgroundColor: ownerState.selected ? theme.palette.bg.interactive.active : 'transparent',
    transition: theme.transitions.create(['background-color', 'color'], { duration: theme.transitions.duration.shortest }),
    '&:hover': {
      color: theme.palette.text.default,
      backgroundColor: ownerState.selected ? theme.palette.bg.interactive.active : theme.palette.bg.interactive.hover,
    },
    '&.Mui-focusVisible': focusRing(theme),
  })
)

const IconSlot = styled('span')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: ICON_SLOT_SIZE,
  height: ICON_SLOT_SIZE,
  '& svg': { width: '1.125rem', height: '1.125rem' },
})

const Label = styled('span')({
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

const CountDot = styled(StatusDot)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1.5),
  right: theme.spacing(1.5),
}))

/** The row without the rail's tooltip, for a `NavGroup` header: its flyout names it on the rail. */
export const NavItemRow = forwardRef<HTMLButtonElement, NavItemRowProps>(function NavItemRow(
  { icon, label, selected = false, count, badge, href, LinkComponent, ...buttonProps },
  ref
) {
  const { collapsed } = useSideNavContext()
  const ownerState: OwnerState = { selected, collapsed }
  const hasCount = count != null && count > 0
  const linkProps = href == null ? {} : { href, LinkComponent }
  // The rail shows only the icon and a dot, so its name carries the count the dot stands for. Set after the
  // spread props, as the rail's Tooltip hands its child the bare label.
  const railLabel = hasCount ? `${label}, ${count}` : label

  return (
    <NavItemRoot
      ref={ref}
      ownerState={ownerState}
      disableRipple
      {...linkProps}
      {...buttonProps}
      aria-label={collapsed ? railLabel : undefined}
    >
      <IconSlot aria-hidden>{icon}</IconSlot>
      {collapsed ? (
        hasCount && <CountDot color="error" aria-hidden />
      ) : (
        <>
          <Label>{label}</Label>
          {hasCount && <CountBadge count={count} />}
          {badge != null && <CountBadge count={badge} color="mint" />}
        </>
      )}
    </NavItemRoot>
  )
})

/**
 * One row of the app's side navigation: a link when given `href`, otherwise a button. On the nav's icon
 * rail it shows only its icon, with the label in a tooltip. Use it outside a `NavList`, such as Settings in
 * the nav's footer; inside one, use `NavItem`.
 */
export const NavItemButton = forwardRef<HTMLButtonElement, NavItemProps>(function NavItemButton(props, ref) {
  const { collapsed } = useSideNavContext()
  // The item itself is the open page, link or not; a group header that holds it is not, so it sets none.
  const row = <NavItemRow ref={ref} {...props} aria-current={props.selected ? 'page' : undefined} />

  return collapsed ? (
    <Tooltip title={props.label} placement="right">
      {row}
    </Tooltip>
  ) : (
    row
  )
})

/** One entry of a `NavList`: a `NavItemButton` in its list item. */
export const NavItem = (props: NavItemProps) => (
  <li>
    <NavItemButton {...props} />
  </li>
)
